import { LoadingButton } from "@mui/lab";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { InputProfile } from "../../components/profile";

// Function to generate the PHP code
const generatePHPCode = (
  targetRequest,
  metaTitle,
  metaDescription
) => `function register_metadatas_in_rest_api() {
    $meta_fields = array(
        '${metaTitle}'     => 'string',
        '${metaDescription}'  => 'string',
        '${targetRequest}'  => 'string'
    );

    foreach ( $meta_fields as $meta_key => $meta_type ) {
        register_post_meta( '', $meta_key, array(
            'show_in_rest' => true,
            'single'       => true,
            'type'         => $meta_type,
            'auth_callback' => function( $allowed, $meta_key, $post_id, $user ) {
              return current_user_can( 'edit_post', $post_id );
            }
        ) );
    }
}
add_action( 'rest_api_init', 'register_metadatas_in_rest_api' );`;

function CodeSnippet({ targetRequest, metaTitle, metaDescription }) {
  const phpCode = generatePHPCode(targetRequest, metaTitle, metaDescription);

  return (
    <pre className="bg-gray_local-400/50 border border-black p-4 rounded overflow-x-auto text-xs">
      <code>{phpCode}</code>
    </pre>
  );
}

const WordpressCodeIntegration = ({ props, createLoading }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const plugins = [
    {
      name: "Sélectionne ton plugin SEO",
      value: null,
    },
    {
      name: "YoastSEO",
      value: "yoast-seo",
      text: "Copie ce code et colle-le dans le fichier functions.php de ton site Wordpress :",
      metaTitle: `_yoast_wpseo_title`,
      metaDescription: `_yoast_wpseo_metadesc`,
      targetRequest: `_yoast_wpseo_focuskw`,
    },
    {
      name: "Rankmath",
      value: "rankmath",
      text: "Copie ce code et colle-le dans le fichier functions.php de ton site Wordpress :",
      metaTitle: `rank_math_title`,
      metaDescription: `rank_math_description`,
      targetRequest: `rank_math_focus_keyword`,
    },
    {
      name: "Autre",
      value: "other",
      text: "Si tu connais les noms des champs métatitre/métadescription utilisés par ton plugin, renseigne-les ci-dessous, copie le code et colle le dans le fichier functions.php de ton site Wordpress :",
      additionalText:
        "Sinon, tu peux directement nous demander pour que l'on intègre le plugin que tu utilises sur le groupe WhatsApp de BotBlogR !",
    },
  ];

  const handleCopyToClipboard = () => {
    const code = generatePHPCode(
      props.values.target_request_field_name,
      props.values.meta_title_field_name,
      props.values.meta_description_field_name
    );
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(null), 1500);
  };

  const selectedPlugin = plugins.find(
    (p) => p.value === props.values.seo_plugin
  );

  // Update meta title and description when plugin changes
  useEffect(() => {
    const selectedPlugin = plugins.find(
      (p) => p.value === props.values.seo_plugin
    );
    if (selectedPlugin?.value) {
      props.setFieldValue(
        "meta_title_field_name",
        selectedPlugin.metaTitle || props.values.meta_title_field_name || ""
      );
      props.setFieldValue(
        "meta_description_field_name",
        selectedPlugin.metaDescription ||
          props.values.meta_description_field_name ||
          ""
      );
      props.setFieldValue(
        "target_request_field_name",
        selectedPlugin.targetRequest ||
          props.values.target_request_field_name ||
          ""
      );
    }
  }, []);

  // Handle plugin change
  const handlePluginChange = (e) => {
    const selectedValue = e.target.value;
    const selectedPlugin = plugins.find((p) => p.value === selectedValue);

    props.setFieldValue("seo_plugin", selectedValue);

    if (selectedPlugin?.value && selectedPlugin.value !== "other") {
      props.setFieldValue(
        "meta_title_field_name",
        selectedPlugin.metaTitle || ""
      );
      props.setFieldValue(
        "meta_description_field_name",
        selectedPlugin.metaDescription || ""
      );
      props.setFieldValue(
        "target_request_field_name",
        selectedPlugin.targetRequest || ""
      );
    } else {
      props.setFieldValue("meta_title_field_name", "");
      props.setFieldValue("meta_description_field_name", "");
      props.setFieldValue("target_request_field_name", "");
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* PLATFORM SELECTION */}
      <div className="mt-2 flex flex-col gap-4">
        <p>
          Choisissez votre plugin SEO pour terminer de configurer l'export des
          métadonnées.
        </p>

        <select
          className="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
          id="seo_plugin"
          name="seo_plugin"
          value={props.values.seo_plugin}
          onChange={handlePluginChange}
        >
          {plugins?.map((p) => (
            <option key={p.value} value={p.value}>
              {p.name}
            </option>
          ))}
        </select>
      </div>

      {/* PLATFORM VALUE */}
      <div>
        {selectedPlugin?.value && (
          <div className="flex flex-col gap-4">
            <p className="text-sm">{selectedPlugin?.text}</p>

            {selectedPlugin?.value === "other" && (
              <div className={`sm:flex sm:space-x-4`}>
                {/* Meta Title Field */}
                <div className="sm:w-1/2 w-full mb-4 sm:mb-0">
                  <InputProfile
                    formikProps={props}
                    type="text"
                    text="Champ du méta titre"
                    name="meta_title_field_name"
                    placeholder="mon_champ_meta_titre"
                  />
                </div>

                {/* Meta Description Field */}
                <div className="sm:w-1/2 w-full">
                  <InputProfile
                    formikProps={props}
                    type="text"
                    text="Champ de la méta description"
                    name="meta_description_field_name"
                    placeholder="mon_champ_meta_description"
                  />
                </div>

                {/* Seo Request Field */}
                <div className="sm:w-1/2 w-full">
                  <InputProfile
                    formikProps={props}
                    type="text"
                    text="Champ de la requête cible"
                    name="target_request_field_name"
                    placeholder="mon_champ_requête_cible"
                  />
                </div>
              </div>
            )}

            <div>
              <div className="flex justify-end mb-2">
                <div className="relative">
                  <button
                    onClick={handleCopyToClipboard}
                    className="bg-gray_local-400 hover:bg-gray_local-400/70 text-white text-sm font-light px-3 py-1 rounded flex items-center"
                  >
                    Copier
                  </button>

                  {copied && (
                    <div className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 flex flex-col items-center">
                      <span className="bg-slate-600/80 text-white text-sm rounded-md py-1 px-2 min-w-36 text-center shadow-lg">
                        {t("Copied!", "Copié !")}
                      </span>
                      <div
                        className="w-3 h-2 bg-slate-600/80"
                        style={{
                          clipPath: "polygon(50% 100%, 0% 0%, 100% 0%)",
                        }}
                      ></div>
                    </div>
                  )}
                </div>
              </div>
              <CodeSnippet
                targetRequest={props.values.target_request_field_name}
                metaTitle={props.values.meta_title_field_name}
                metaDescription={props.values.meta_description_field_name}
              />
            </div>

            {selectedPlugin?.value === "other" && (
              <p className="text-sm">{selectedPlugin?.additionalText}</p>
            )}
          </div>
        )}
      </div>

      <LoadingButton
        type="submit"
        loading={createLoading || props.isSubmitting}
        variant="contained"
        sx={{
          textTransform: "none",
          bgcolor: "#8b5cf6",
          "&:hover": {
            bgcolor: "#7c3aed",
          },
        }}
        onClick={props.handleSubmit}
      >
        Valider la configuration WordPress
      </LoadingButton>
    </div>
  );
};

export default WordpressCodeIntegration;
